import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  BooleanInput,
  Form,
  Loading,
  ReferenceInput,
  SelectInput,
  TextInput,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { type AccessibleRegion } from '../auth/permissions';
import type { AdminDataProvider, Charity, Market } from '../dataProvider';
import { validUrl } from './validators';

/**
 * Charity edit. All descriptive fields are editable plus the ACTIVE toggle — the
 * soft-disable control (D-charity-softdelete). There is NO delete button: a
 * charity is deactivated (active=false → status 'inactive'), never destroyed,
 * because past donations reference it.
 *
 * `market` is set at creation and shown READ-ONLY here — a charity does not move
 * markets, and changing it would break the region scoping of its donations.
 *
 * Audit for free (#25): saving calls `dataProvider.update('charities')`; the
 * wrapper records the diff (incl. status: active → inactive on a deactivate).
 * The route is gated `charities:write`; region-scoped (cannot edit a charity
 * outside the admin's market, even by direct URL).
 */

interface CharityEditValues {
  name: string;
  charityTypeId: number;
  description: string;
  logoUrl: string;
  bannerUrl: string;
  active: boolean;
}

const CharityEditForm = ({ record }: { record: Charity }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const defaultValues: CharityEditValues = {
    name: record.name,
    charityTypeId: record.charityTypeId,
    description: record.description,
    logoUrl: record.logoUrl ?? '',
    bannerUrl: record.bannerUrl ?? '',
    active: record.status === 'active',
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as CharityEditValues;
    const data = {
      name: values.name,
      charityTypeId: Number(values.charityTypeId),
      description: values.description,
      logoUrl: values.logoUrl,
      bannerUrl: values.bannerUrl,
      status: values.active ? 'active' : 'inactive',
    };
    const previousData = {
      name: record.name,
      charityTypeId: record.charityTypeId,
      description: record.description,
      logoUrl: record.logoUrl ?? '',
      bannerUrl: record.bannerUrl ?? '',
      status: record.status,
    };

    try {
      if (JSON.stringify(data) !== JSON.stringify(previousData)) {
        await dataProvider.update('charities', {
          id: record.id,
          data,
          previousData,
        });
      }
      notify('Charity updated', { type: 'success' });
      redirect('show', 'charities', record.id);
    } catch {
      notify('Could not save the charity', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit {record.name}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="name"
              label="Name"
              validate={required('Name is required')}
              fullWidth
            />
            {/* Market is fixed at creation — read-only here. */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Market (set at creation)
              </Typography>
              <Typography variant="body1">
                {record.market.toUpperCase()}
              </Typography>
            </Box>
            <ReferenceInput
              source="charityTypeId"
              reference="charityTypes"
              sort={{ field: 'label', order: 'ASC' }}
              perPage={100}
            >
              <SelectInput
                label="Charity type"
                optionText="label"
                validate={required('Charity type is required')}
              />
            </ReferenceInput>
            <TextInput
              source="description"
              label="Description"
              validate={required('Description is required')}
              multiline
              fullWidth
            />
            <TextInput
              source="logoUrl"
              label="Logo URL (optional)"
              validate={validUrl}
              fullWidth
            />
            <TextInput
              source="bannerUrl"
              label="Banner URL (optional)"
              validate={validUrl}
              fullWidth
            />
            {/* The soft-disable control — deactivate/reactivate (no delete). */}
            <BooleanInput source="active" label="Active" />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          <Button
            type="button"
            onClick={() => redirect('show', 'charities', record.id)}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const CharityEditView = () => {
  const { id } = useParams();
  const { accessibleRegions } = useRegion();
  const { data: record, isLoading } = useGetOne<Charity>('charities', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;

  // Region guard — cannot edit a charity outside the admin's market.
  const inScope = accessibleRegions.includes(
    (record.market as Market).toUpperCase() as AccessibleRegion,
  );
  if (!inScope) return <NotAuthorized />;

  return <CharityEditForm record={record} />;
};

/** Gated entry point registered as the `charities` Resource's edit. */
export const CharityEdit = () => (
  <RequireAccess capability="charities:write">
    <CharityEditView />
  </RequireAccess>
);
